import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import assignmentAttachmentServices from 'ApiService/Placement/assignmentAttachmentServices';

const UploadForm = ({ assignmentId, onUploaded }) => {
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sharing, setSharing] = useState(false);
  const fileInputRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const awaitPreviews = async (fileList, existing) => {
    const entries = [...existing];
    for (const f of fileList) {
      if (f.type.startsWith('image/')) {
        const url = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(f);
        });
        entries.push({ name: f.name, url });
      } else {
        entries.push({ name: f.name, url: null });
      }
    }
    return entries;
  };

  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const newFiles = [...files, ...selected];
    setFiles(newFiles);
    const newPreviews = await awaitPreviews(selected, previews);
    setPreviews(newPreviews);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleShare = async () => {
    if (!files.length && !audioBlob && !notes.trim()) { toast.error('Nothing to share'); return; }
    setSharing(true);
    try {
      const fd = new FormData();
      fd.append('assignmentId', assignmentId);
      fd.append('notes', notes);

      for (const f of files) {
        fd.append('files', f);
      }
      if (audioBlob) {
        fd.append('files', audioBlob, `voice-note-${Date.now()}.webm`);
      }

      const res = await assignmentAttachmentServices.batchUpload(fd);
      if (res.data.success) {
        toast.success('Shared successfully');
        onUploaded(res.data.data || []);
      }
      setFiles([]);
      setPreviews([]);
      setAudioBlob(null);
      setAudioUrl(null);
      setNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setSharing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      setRecordingTime(0);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start();
      setRecording(true);
      timerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setAudioUrl(null);
  };

  const formatTime = (s) => {
    const min = String(Math.floor(s / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${min}:${sec}`;
  };

  const hasContent = files.length > 0 || audioBlob || notes.trim().length > 0;

  return (
    <div className="space-y-2">
      {/* Preview chips */}
      {(previews.length > 0 || audioUrl) && (
        <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-white/10 dark:bg-navy-700">
          {previews.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm dark:bg-navy-800 dark:text-gray-200">
              <span>📎</span>
              <span className="max-w-[120px] truncate">{p.name}</span>
              <button onClick={() => removeFile(i)} className="ml-1 text-gray-400 hover:text-red-500" title="Remove">&times;</button>
            </span>
          ))}
          {audioUrl && (
            <span className="inline-flex items-center gap-2 rounded bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm dark:bg-navy-800 dark:text-gray-200">
              <span>🎙</span>
              <audio controls src={audioUrl} className="h-6 max-w-[140px]" />
              <button onClick={cancelRecording} className="text-gray-400 hover:text-red-500" title="Remove">&times;</button>
            </span>
          )}
        </div>
      )}

      {/* Recording indicator (shown instead of chips while recording) */}
      {recording && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-700 dark:bg-red-900/20">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
          <span className="font-mono text-sm font-bold text-red-600">{formatTime(recordingTime)}</span>
        </div>
      )}

      {/* Chat input bar */}
      <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 dark:border-white/20 dark:bg-navy-700">
        {/* Attach button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={sharing}
          className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-600 dark:hover:text-gray-200 disabled:opacity-50"
          title="Attach files"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,audio/*,.pdf,.docx,.txt,.pptx" onChange={handleFileSelect} className="hidden" />

        {/* Notes input */}
        <input
          type="text"
          placeholder="Type notes here..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none placeholder-gray-400 dark:text-white"
        />

        {/* Voice record button */}
        {!recording ? (
          <button
            onClick={startRecording}
            disabled={sharing}
            className="shrink-0 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-500 dark:hover:bg-navy-600 dark:hover:text-red-400 disabled:opacity-50"
            title="Record voice note"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="shrink-0 rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Stop recording"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        )}

        {/* Send button */}
        <button
          onClick={handleShare}
          disabled={sharing || !hasContent}
          className="flex shrink-0 items-center justify-center rounded-lg bg-brand-500 p-1.5 text-white hover:bg-brand-600 disabled:opacity-50"
          title="Send"
        >
          {sharing ? (
            <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default UploadForm;